import { useEffect, useState } from 'react';
import { doc, onSnapshot } from 'firebase/firestore';
import { db } from '@/config/firebase';

interface WorkspacePayoutStatus {
  isLoading: boolean;
  pendingPayoutBalanceCents: number;
  pendingPayoutCount: number;
  hasConnectAccount: boolean;
}

const INITIAL_STATUS: WorkspacePayoutStatus = {
  isLoading: true,
  pendingPayoutBalanceCents: 0,
  pendingPayoutCount: 0,
  hasConnectAccount: false,
};

export function useWorkspacePayoutStatus(workspaceId?: string | null): WorkspacePayoutStatus {
  const [status, setStatus] = useState<WorkspacePayoutStatus>(INITIAL_STATUS);

  useEffect(() => {
    if (!workspaceId) {
      setStatus({
        ...INITIAL_STATUS,
        isLoading: false,
      });
      return;
    }

    const workspaceRef = doc(db, 'workspaces', workspaceId);
    const unsubscribe = onSnapshot(
      workspaceRef,
      (snapshot) => {
        if (!snapshot.exists()) {
          setStatus({
            isLoading: false,
            pendingPayoutBalanceCents: 0,
            pendingPayoutCount: 0,
            hasConnectAccount: false,
          });
          return;
        }

        const data = snapshot.data() as {
          pendingPayoutBalanceCents?: number;
          pendingPayoutCount?: number;
          stripeConnectAccountId?: string | null;
        };

        setStatus({
          isLoading: false,
          pendingPayoutBalanceCents: Number(data.pendingPayoutBalanceCents || 0),
          pendingPayoutCount: Number(data.pendingPayoutCount || 0),
          hasConnectAccount: Boolean(data.stripeConnectAccountId),
        });
      },
      (error) => {
        console.error('Failed to listen to workspace payout status', error);
        setStatus((prev) => ({ ...prev, isLoading: false }));
      }
    );

    return () => {
      unsubscribe();
    };
  }, [workspaceId]);

  return status;
}
